/* eslint-disable max-lines */
// 9fbef606107a605d69c0edbcd8029e5d
import isEmpty from 'lodash/isEmpty';
import groupBy from 'lodash/groupBy';
import logger from '@tcp/core/src/utils/loggerInstance';
import { MAX_NO_OF_ESG_BADGES_TO_BE_DISPLAYED } from '@tcp/core/src/constants/numeric.constants';
import { ESG_BADGE_ANALYTICS_STRING } from '@tcp/core/src/constants/badge.constants';
import { routerPush, getViewportInfo } from '@tcp/core/src/utils';
import { getVideoUrl } from '../views/ProductGridItem.util';
import { getStyliticsUserName } from '../../../../../../../utils/utils';

/**
 * @return the first element in the getMapSliceForColor(colorFitsSizesMap, colorName).fits array that corresponds to the given fit.
 * If there are no fits associated with the given color, then the first element in the array is returned.
 */
export function getMapSliceForFit(colorFitsSizesMap, colorName, fitName) {
  // eslint-disable-next-line
  const currentColorEntry = getMapSliceForColor(colorFitsSizesMap, colorName);
  if (!currentColorEntry) {
    return;
  }
  if (currentColorEntry.hasFits) {
    // eslint-disable-next-line
    return currentColorEntry.fits.find((entry) => {
      return entry.fitName === fitName || entry.fitNameVal === fitName;
    });
  }
  // eslint-disable-next-line
  return currentColorEntry.fits && currentColorEntry.fits.length > 0 && currentColorEntry.fits[0];
}

export const getTopBadge = (isMatchingFamily, badge1) => {
  return isMatchingFamily && badge1 && badge1.matchBadge
    ? badge1 && badge1.matchBadge
    : badge1 && badge1.defaultBadge;
};

export const getParsedVariantDetails = (variantDetails) => {
  if (!variantDetails) {
    return null;
  }
  try {
    const replacedVariantDetails = variantDetails?.replace(/'/g, '"');
    return JSON.parse(replacedVariantDetails);
  } catch (err) {
    return null;
  }
};

export const getPayloadForOneClickATC = ({
  name,
  isGiftCard,
  parsedVariantDetails,
  basicImageUrl,
  seoToken,
  productId,
  TCPStyleType,
  TCPStyleQTY,
  listPrice,
  offerPrice,
  TCPWebOnlyFlagStore,
  TCPProductIndStore,
  prodPartNo,
  brand,
  topBadge,
  bossDisabledFlags,
  colorName,
  hardMarkDown,
  fromPage,
}) => {
  const { UPC_ID, size } = parsedVariantDetails || {};
  return {
    isGiftCard,
    productName: name,
    skuInfo: {
      imageUrl: basicImageUrl,
      color: { name: colorName },
      variantId: UPC_ID,
      productId,
      seoToken,
      size,
      fit: null,
      TCPStyleType,
      TCPStyleQTY,
      listPrice,
      offerPrice,
      TCPWebOnlyFlagStore,
      TCPProductIndStore,
      prodPartNo,
    },
    fromPage,
    originPage: 'plp',
    brand,
    quantity: 1,
    hardMarkDown,
    isOnlineExclusive: topBadge || null,
    bossDisabledFlags,
    isOpenNewATBModal: true,
  };
};

export function getMapSliceForSize(colorFitsSizesMap, colorName, fitName, sizeName) {
  const currentFitEntry = getMapSliceForFit(colorFitsSizesMap, colorName, fitName);
  if (!currentFitEntry) return;
  // eslint-disable-next-line
  return currentFitEntry.sizes.find((entry) => entry.sizeName === sizeName);
}

/**
 * @return the first element in the colorFitsSizesMap array that corresponds to the given colorName.
 */
export function getMapSliceForColor(colorFitsSizesMap, colorName) {
  return colorFitsSizesMap && colorFitsSizesMap.find((entry) => entry.color.name === colorName);
}

export function filterCurrentSelectedProduct(allProductId, getInitialFormVal) {
  return allProductId && allProductId.find((entry) => entry.auxdescription === getInitialFormVal);
}

export function getIconImageForColor(productInfo, colorId) {
  if (!productInfo.imagesByColor) return null;

  const imagesByColorEntry =
    productInfo.imagesByColor[colorId] ||
    productInfo.imagesByColor[Object.keys(productInfo.imagesByColor)[0]];
  return imagesByColorEntry.extraImages && imagesByColorEntry.extraImages[0]
    ? imagesByColorEntry.extraImages[0].iconSizeImageUrl
    : imagesByColorEntry.basicImageUrl;
}

export function getSkuId(colorFitsSizesMap, color, fit, size) {
  const currentSizeEntry = getMapSliceForSize(colorFitsSizesMap, color, fit, size);
  return currentSizeEntry && currentSizeEntry.skuId;
}

/**
 * @return the variant id selected by the user.
 */
export function getVariantId(colorFitsSizesMap, color, fit, size) {
  const currentSizeEntry = getMapSliceForSize(colorFitsSizesMap, color, fit, size);
  return currentSizeEntry && currentSizeEntry.variantId;
}

/**
 * @return the variant no selected by the user.
 */
export function getVariantNo(colorFitsSizesMap, color, fit, size) {
  const currentSizeEntry = getMapSliceForSize(colorFitsSizesMap, color, fit, size);
  return currentSizeEntry && currentSizeEntry.variantNo;
}

/**
 * Returns the list and offer prices corresponding to the sku with the given color, fit and size.
 */
export function getPrices(productInfo, color, fit, size) {
  const currentSizeEntry = getMapSliceForSize(productInfo.colorFitsSizesMap, color, fit, size);
  const currentColorEntry = getMapSliceForColor(productInfo.colorFitsSizesMap, color);
  if (currentSizeEntry && currentSizeEntry.listPrice) {
    return {
      listPrice: currentSizeEntry.listPrice,
      offerPrice: currentSizeEntry.offerPrice,
      badge2: currentColorEntry.miscInfo.badge2,
    };
  }

  if (currentColorEntry && currentColorEntry.listPrice) {
    return {
      listPrice: currentColorEntry.listPrice,
      offerPrice: currentColorEntry.offerPrice,
      badge2: currentColorEntry.miscInfo.badge2,
    };
  }

  return { listPrice: productInfo.listPrice, offerPrice: productInfo.offerPrice };
}

/**
 * Returns the list and offer prices corresponding to the sku with the given color, fit and size.
 */
export function getPricesWithRange(productInfo, color, fit, size, isSelectedSizeDisabled) {
  const currentSizeEntry = getMapSliceForSize(productInfo.colorFitsSizesMap, color, fit, size);
  const currentColorEntry = getMapSliceForColor(productInfo.colorFitsSizesMap, color);
  if (currentSizeEntry && currentSizeEntry.listPrice && !isSelectedSizeDisabled) {
    return { listPrice: currentSizeEntry.listPrice, offerPrice: currentSizeEntry.offerPrice };
  }

  if (
    currentColorEntry &&
    currentColorEntry.listPrice &&
    !(
      (productInfo.highOfferPrice && productInfo.lowOfferPrice) ||
      (productInfo.highListPrice && productInfo.lowListPrice)
    )
  ) {
    return {
      listPrice: currentColorEntry.listPrice,
      offerPrice: currentColorEntry.offerPrice,
    };
  }

  return {
    listPrice: productInfo.lowListPrice,
    offerPrice: productInfo.lowOfferPrice,
    highListPrice: productInfo.highListPrice || null,
    highOfferPrice: productInfo.highOfferPrice || null,
    onlyListPrice: productInfo.listPrice || null,
  };
}

export function getPriceFromPreviousPrice(prevPrice, priceValue, price) {
  if (prevPrice) {
    return prevPrice + price;
  }
  return priceValue + price;
}

/**
 * @return the first element in the colorFitsSizesMap array that corresponds to the given colorProductId.
 */
export function getMapSliceForColorProductId(colorFitsSizesMap, colorProductId) {
  const selectedProduct =
    colorFitsSizesMap &&
    colorFitsSizesMap.find(
      (entry) => entry.colorProductId === colorProductId || entry.colorDisplayId === colorProductId
    );
  return (
    selectedProduct ||
    (colorFitsSizesMap && colorFitsSizesMap.length > 0 ? colorFitsSizesMap[0] : null)
  );
}

/**
 * @return the first element in the colorFitsSizesMap array that corresponds to the given colorProductId.
 */
export function getMapSliceForSizeSkuID(colorProduct, size) {
  let skuId;
  if (colorProduct && colorProduct.fits && Array.isArray(colorProduct.fits)) {
    for (let i = 0; i < colorProduct.fits.length; i += 1) {
      const fitsMap = colorProduct.fits[i];
      for (let j = 0; j < fitsMap.sizes.length; j += 1) {
        const sizesMap = fitsMap.sizes[j];
        if (sizesMap.sizeName === size) {
          skuId = sizesMap;
          break;
        }
      }
    }
  }

  return skuId;
}

/**
 * @return the element flagged as default (or the first one) on the fits array
 */
export function getDefaultFitForColorSlice(colorFitsSizesMapEntry, ignoreQtyCheck = false) {
  return (
    colorFitsSizesMapEntry.fits.find(
      (fit) => !ignoreQtyCheck && fit.isDefault && fit.maxAvailable > 0
    ) ||
    colorFitsSizesMapEntry.fits.find((fit) => !ignoreQtyCheck && fit.maxAvailable > 0) ||
    colorFitsSizesMapEntry.fits[0]
  );
}

/**
 * @return if the product has a single fit with a single size common to all colors then return that common sizeName;
 * otherwise return the empty string.
 */
export function getDefaultSizeForProduct(colorFitsSizesMap) {
  const firstSizeName = colorFitsSizesMap[0].fits[0].sizes[0].sizeName;
  // eslint-disable-next-line
  for (let colorEnrtry of colorFitsSizesMap) {
    if (
      colorEnrtry.fits.length > 1 ||
      colorEnrtry.fits[0].sizes.length > 1 ||
      colorEnrtry.fits[0].sizes[0].sizeName !== firstSizeName
    ) {
      return '';
    }
  }
  return firstSizeName;
}

const getIsColorOnModelLegible = (curentColorEntry) =>
  curentColorEntry && curentColorEntry.miscInfo && curentColorEntry.miscInfo.hasOnModelAltImages;

/**
 * @summary This function will return an array of image paths to display
 * @param {Object} args
 * @param {Object} args.imagesByColor - the maping object holding all color's images
 * @param {Object} args.curentColorEntry - Object holding info of the currently selected Color
 * @param {Object} args.isAbTestActive - AB test flag to know if we should display
 * @param {Object} args.isFullSet - If true it will return all data from imagesByColor for given selection
 */
const getAltImages = (mainAndAltImages, excludeShortImage) => {
  return groupBy(mainAndAltImages, (img) => {
    if (!(excludeShortImage && img.isShortImage)) {
      return img.isOnModalImage ? 'onModelAltImages' : 'regularAltImages';
    }
    return 'shortImage';
  });
};

const getCurrentProductSelectedColor = (imagesByColor, curentColorEntry) => {
  const color = curentColorEntry?.color?.name || Object.keys(imagesByColor)[0];
  // Workaround for bad data being passed:
  if (imagesByColor[color] === undefined) return false;
  return imagesByColor[color].extraImages;
};

const imageDoubleFallback = (altImageInput) => {
  // This case is already covered by the "catch" block in getImagesToDisplay, but
  // this provides a second fallback for an improperly supplied "curentColorEntry"
  // by generating a basic altImages object
  const fakeAIString = `${altImageInput.split('_')[0]}/${altImageInput}.jpg`;
  const fakeObject = {
    isShortImage: false,
    isOnModalImage: false,
    iconSizeImageUrl: fakeAIString,
    listingSizeImageUrl: fakeAIString,
    regularSizeImageUrl: fakeAIString,
    bigSizeImageUrl: fakeAIString,
    superSizeImageUrl: fakeAIString,
  };
  return [fakeObject];
};

export const getImagesToDisplay = (args) => {
  const { imagesByColor, curentColorEntry, isAbTestActive, isFullSet, excludeShortImage } = args;
  let images = [];
  try {
    // See DTN-155 for image suffix value definitions)
    const mainAndAltImages = isEmpty(imagesByColor)
      ? null
      : getCurrentProductSelectedColor(imagesByColor, curentColorEntry);

    const isColorOnModelLegible = getIsColorOnModelLegible(curentColorEntry);

    const altImages =
      mainAndAltImages !== false
        ? getAltImages(mainAndAltImages, excludeShortImage)
        : getAltImages(imageDoubleFallback(curentColorEntry.colorProductId), excludeShortImage);

    const { regularAltImages = null, onModelAltImages = null } = altImages;

    const imagesToDisplay =
      isAbTestActive && isColorOnModelLegible ? onModelAltImages : regularAltImages;
    // eslint-disable-next-line
    images = isFullSet
      ? imagesToDisplay
      : imagesToDisplay
      ? imagesToDisplay.map((imgData) => imgData.regularSizeImageUrl)
      : [];
  } catch (error) {
    logger.error(
      "ProductsGridItem: Backend sent us a bad color name so we don't know what image set to map to, see aux description in API call"
    );
  }
  return images;
};

export const getPrimaryImages = (args) => {
  const { imagesByColor, curentColorEntry, isAbTestActive, excludeShortImage } = args;
  let images = [];

  try {
    // See DTN-155 for image suffix value definitions
    const imageColorNames = Object.keys(imagesByColor);
    return (
      imageColorNames &&
      imageColorNames.map((colorName) => {
        const mainAndAltImages = isEmpty(imagesByColor)
          ? null
          : imagesByColor[colorName].extraImages;
        const isColorOnModelLegible = getIsColorOnModelLegible(curentColorEntry);
        const altImages = getAltImages(mainAndAltImages, excludeShortImage);
        const { regularAltImages = null, onModelAltImages = null } = altImages;
        const imagesToDisplay =
          isAbTestActive && isColorOnModelLegible ? onModelAltImages : regularAltImages;
        images = imagesToDisplay
          ? imagesToDisplay.map((imgData) => imgData.regularSizeImageUrl)
          : [];
        return images[0];
      })
    );
  } catch (error) {
    logger.error(
      "ProductsGridItem: Backend sent us a bad color name so we don't know what image set to map to, see auxdescription in API call"
    );
  }
  return images && images[0];
};

export const checkIsSelectedSizeDisabled = (productInfo, formData) => {
  if (formData.color && formData.size) {
    const currentFitInfo = getMapSliceForFit(
      productInfo.colorFitsSizesMap,
      formData.color,
      formData.fit
    );
    const sizesArray = currentFitInfo ? currentFitInfo.sizes : [];
    const currentSizeObj = sizesArray.filter((sizeObj) => sizeObj.sizeName === formData.size);
    return currentSizeObj.length ? currentSizeObj[0].maxAvailable <= 0 : true;
  }
  return false;
};

export const checkAndGetDefaultFitName = (fitName, colorName, colorFitsSizesMap) => {
  let defaultFitName = '';
  if (!fitName) {
    const currentColorInfo = getMapSliceForColor(colorFitsSizesMap, colorName);
    if (currentColorInfo && currentColorInfo.hasFits) {
      defaultFitName = getDefaultFitForColorSlice(currentColorInfo).fitName;
    }
  } else {
    return fitName;
  }
  return defaultFitName;
};

export const getFormattedLoyaltyText = (text) => {
  return text.replace(/\s+/g, ' ').trim();
};

export const getDefaultSizes = (formValues, productInfo, isShowDefaultSize) => {
  let showDefaultSizeMsg = false;
  const defaultSelection = {
    fit: null,
    size: null,
  };

  if (productInfo.categoryId && isShowDefaultSize) {
    // eslint-disable-next-line extra-rules/no-commented-out-code
    // defaultSelection = getCustomerSelection(productInfo.categoryId);
    showDefaultSizeMsg = !!(defaultSelection.size || defaultSelection.fit);
  }

  const formValuesWithDefaultSizes = {
    ...formValues,
    fit: defaultSelection.fit ? defaultSelection.fit : formValues.fit,
    size: defaultSelection.size ? defaultSelection.size : formValues.size,
  };
  const isSelectedSizeDisabled = checkIsSelectedSizeDisabled(
    productInfo,
    formValuesWithDefaultSizes
  );
  return isSelectedSizeDisabled ||
    (defaultSelection.size && formValues.size && defaultSelection.size !== formValues.size)
    ? { showDefaultSizeMsg: false, formValues }
    : { showDefaultSizeMsg, formValues: formValuesWithDefaultSizes };
};

/**
 * @method isProductOOS
 * @description checks if the selected size variant is having available
 * quantity
 */
export const isProductOOS = (colorFitsSizesMap, selectedSKu) => {
  const currentFitEntry = getMapSliceForFit(colorFitsSizesMap, selectedSKu.color, selectedSKu.Fit);
  if (currentFitEntry && currentFitEntry.sizes) {
    const selectedSKuProductInfo = currentFitEntry.sizes.find(
      (size) => size.sizeName === selectedSKu.Size
    );
    const maxAvailableProducts = selectedSKuProductInfo ? selectedSKuProductInfo.maxAvailable : 0;

    return maxAvailableProducts < 1;
  }
  return true;
};

/**
 * @method isBOSSProductOOSQtyMismatched
 * @description checks if the selected size variant is having available
 * quantity
 */
export const isBOSSProductOOSQtyMismatched = (colorFitsSizesMap, selectedSKu) => {
  const currentFitEntry = getMapSliceForFit(colorFitsSizesMap, selectedSKu.color, selectedSKu.fit);
  if (currentFitEntry && currentFitEntry.sizes) {
    const selectedSKuProductInfo = currentFitEntry.sizes.find(
      (size) => size.sizeName === selectedSKu.Size
    );
    const maxAvailableBossProducts = selectedSKuProductInfo
      ? selectedSKuProductInfo.maxAvailableBoss
      : 0;
    const qtyMismatch = selectedSKu.quantity > maxAvailableBossProducts;
    return maxAvailableBossProducts < 1 || qtyMismatch;
  }
  return true;
};

export const getProductListToPath = (str) => {
  if (str && str.indexOf('/b/') !== -1) {
    return `/b?bid=${str.split('/b/')[1]}`;
  }
  if (str && str.split('/p/') !== -1) {
    return `/p?pid=${str.split('/p/')[1]}`;
  }
  return str;
};

export const getProductListToPathInMobileApp = (str) => {
  let searchPath = str;
  if (str && str.indexOf('/p/') !== -1) {
    searchPath = `${str.split('/p/')[1]}`;
  } else if (str && str.indexOf('/b/') !== -1) {
    searchPath = `${str.split('/b/')[1]}`;
  }
  return searchPath;
};

export const getRearrangedImgUrls = (imageUrls, curentColorEntry) => {
  const videoUrl = getVideoUrl(curentColorEntry);
  if (videoUrl) {
    let completeVideoUrl = videoUrl;
    /* contruct complete URL, see if Unboxed can send the complete URL
    then this can be omitted */
    if (curentColorEntry.colorProductId) {
      const videoUrlPrefix = curentColorEntry.colorProductId.split('_')[0];
      completeVideoUrl = `${videoUrlPrefix}/${videoUrl}`;
    }
    // remove duplicate occurance of the video form imgUrls
    const indexOfVideo = imageUrls.indexOf(completeVideoUrl);
    if (indexOfVideo > -1) {
      imageUrls.splice(indexOfVideo, 1);
    }
    // push the video on first slot
    if (imageUrls && Array.isArray(imageUrls)) {
      imageUrls.unshift(completeVideoUrl);
    }
  }
  return imageUrls;
};

export const getSelectedColorData = (colorFitsSizesMap, colorId) => {
  return (
    colorFitsSizesMap &&
    colorFitsSizesMap.filter((colorItem) => {
      return colorItem.colorProductId === colorId;
    })
  );
};

export const getFeatures = (colorFitsSizesMap, colorId, isGiftCard) => {
  const selectedColorElement = getSelectedColorData(colorFitsSizesMap, colorId);

  const hasFits =
    selectedColorElement && selectedColorElement[0] && selectedColorElement[0].hasFits;

  let features = '';

  if (!isGiftCard) {
    features += 'alt images full size image';
  }
  if (hasFits) {
    features += `${features} size chart`;
  }
  return features;
};

export const setSelectedPack = (
  setSelectedMultipack,
  selectedMultipackNumber,
  TCPMultipackProductMapping
) => {
  let defaultSelectedPack = null;
  if (TCPMultipackProductMapping && TCPMultipackProductMapping.length > 0) {
    TCPMultipackProductMapping.forEach((ele) => {
      if (selectedMultipackNumber && Number(selectedMultipackNumber) === Number(ele)) {
        defaultSelectedPack = ele;
      }
    });
  }
  if (defaultSelectedPack != null) {
    setSelectedMultipack(defaultSelectedPack);
  }
};

export const getMapColorList = (TCPStyleColor, colorSlice) => {
  const getVal =
    TCPStyleColor &&
    TCPStyleColor.filter((x) => x.includes(colorSlice && colorSlice.colorDisplayId));
  return getVal && getVal[0] && getVal[0].split('#')[0] && getVal[0].split('#')[0].split('|');
};

export const closeQuickViewModalPopup = (fromPickup, props) => {
  const {
    closeQuickViewModal,
    clearAddToBagError,
    clearMultipleItemsAddToBagError,
    clearCheckoutServerError,
    setCartItemsSflError,
    productInfo,
    setSelectedMultipack,
  } = props;
  if (!fromPickup) {
    closeQuickViewModal();
  }
  clearAddToBagError();
  clearMultipleItemsAddToBagError();
  clearCheckoutServerError({});
  setCartItemsSflError(null);
  const { TCPMultipackProductMapping, TCPStyleType } = productInfo[0]?.product;
  setSelectedPack(setSelectedMultipack, TCPStyleType, TCPMultipackProductMapping);
};

export const getSoftClass = (softDisableMutiPack) => {
  const splitPack = softDisableMutiPack && softDisableMutiPack.split(';');
  return splitPack
    .map((packId) => {
      return packId && parseInt(packId.split('#')[1], 10);
    })
    .sort((a, b) => {
      return a - b;
    });
};

export const getFormValue = (initialFormValues, initialValues) => {
  return (
    (initialFormValues && initialFormValues.color) ||
    (initialValues && initialValues.color && initialValues.color.name)
  );
};

export const checkAvailablePill = (item, softClass, multipackToSelect, singleKey) => {
  return item === singleKey ||
    (item !== singleKey && softClass.includes(item) && item !== multipackToSelect) ||
    (item !== singleKey && !softClass.includes(item) && item === multipackToSelect)
    ? 'enable'
    : 'soft-disable';
};

export const getSoftDisableMutiPack = (getSelectedProductInformation) => {
  return getSelectedProductInformation && getSelectedProductInformation.multipack_mapping
    ? getSelectedProductInformation.multipack_mapping
    : null;
};

export const checkInWishlistUtil = (wishList, productId) => {
  const filterArray =
    wishList?.items?.length &&
    wishList?.items.filter((key) => {
      return key.productInfo.generalProductId === productId;
    });
  if (filterArray?.length > 0) {
    return true;
  }
  return false;
};

/**
 *
 * @param {*} bigSizeImageUrl
 * Sample Input to filterBundleImageUrl function->
 * bigSizeImageUrl: "Kit2800502-1.jpg/Kit2800502-1.jpg"
 * Output->
 * bigSizeImageUrl: "Kit2800502/Kit2800502-1.jpg"
 */
export const filterBundleImageUrl = (bigSizeImageUrl) => {
  if (!bigSizeImageUrl) {
    return '';
  }
  const firstPart = bigSizeImageUrl.split('/')?.[0]?.split('.')?.[0]?.split('-')?.[0];
  const secondPart = bigSizeImageUrl.split('/')?.[1];
  return `${firstPart}/${secondPart}`;
};

export const getIsOnlineExclusive = (miscInfo, isStoreSpecialAvailable) => {
  if (miscInfo && miscInfo.badge1) {
    const { badge1 } = miscInfo;
    return badge1?.defaultBadge && !isStoreSpecialAvailable
      ? badge1.defaultBadge.includes('ONLINE')
      : false;
  }
  return false;
};

export const getIsDefaultProductBadge = (currentColorMiscInfo) => {
  const currentMiscInfo = currentColorMiscInfo || {};
  const { badge1 } = currentMiscInfo;
  let badge1Value;
  if (currentMiscInfo) {
    badge1Value = badge1?.matchBadge ? badge1?.matchBadge : badge1?.defaultBadge;
  }
  return badge1Value && badge1Value.includes('ONLINE');
};

const getAccessoriesPrice = (
  allAccessories,
  maximumOfferPrice,
  maximumListPrice,
  minimumOfferPrice,
  minimumListPrice
) => {
  let maxOfferPrice = maximumOfferPrice;
  let maxListPrice = maximumListPrice;
  let minOfferPrice = minimumOfferPrice;
  let minListPrice = minimumListPrice;
  allAccessories.slice(2).forEach((accessory) => {
    const { productInfo } = accessory;
    const { offerPrice, listPrice } = productInfo;
    if (offerPrice > maxOfferPrice) {
      maxOfferPrice = offerPrice;
    } else if (offerPrice < minOfferPrice) {
      minOfferPrice = offerPrice;
    }
    if (listPrice > maxListPrice) {
      maxListPrice = listPrice;
    } else if (listPrice < minListPrice) {
      minListPrice = listPrice;
    }
  });
  return {
    maxOfferPrice,
    maxListPrice,
    minOfferPrice,
    minListPrice,
  };
};

export const setMinMaxPrice = (allAccessories) => {
  let minOfferPrice;
  let maxOfferPrice;
  let minListPrice;
  let maxListPrice;
  const { productInfo: productInfoZeroIndex } = allAccessories[0];
  const { offerPrice: offerPriceZeroIndex, listPrice: listPriceZeroIndex } = productInfoZeroIndex;
  if (allAccessories.length === 1) {
    maxOfferPrice = offerPriceZeroIndex;
    minOfferPrice = offerPriceZeroIndex;
    maxListPrice = listPriceZeroIndex;
    minListPrice = listPriceZeroIndex;
  } else {
    const { productInfo: productInfoFirstIndex } = allAccessories[1];
    const { offerPrice: offerPriceFirstIndex, listPrice: listPriceFirstIndex } =
      productInfoFirstIndex;
    // If more than 1 accessory for the main product is present
    if (offerPriceZeroIndex > offerPriceFirstIndex) {
      maxOfferPrice = offerPriceZeroIndex;
      minOfferPrice = offerPriceFirstIndex;
    } else {
      maxOfferPrice = offerPriceFirstIndex;
      minOfferPrice = offerPriceZeroIndex;
    }
    if (listPriceZeroIndex > listPriceFirstIndex) {
      maxListPrice = listPriceZeroIndex;
      minListPrice = listPriceFirstIndex;
    } else {
      maxListPrice = listPriceFirstIndex;
      minListPrice = listPriceZeroIndex;
    }
    // If more than 2 accessory for the main product is present
    if (allAccessories.length > 2) {
      const {
        maxOfferPrice: maximumOfferPrice,
        minOfferPrice: minimumOfferPrice,
        maxListPrice: maximumListPrice,
        minListPrice: minimumListPrice,
      } = getAccessoriesPrice(
        allAccessories,
        maxOfferPrice,
        maxListPrice,
        minOfferPrice,
        minListPrice
      );
      maxOfferPrice = maximumOfferPrice;
      maxListPrice = maximumListPrice;
      minOfferPrice = minimumOfferPrice;
      minListPrice = minimumListPrice;
    }
  }
  return {
    minOfferPrice,
    maxOfferPrice,
    minListPrice,
    maxListPrice,
  };
};

export const getSecondaryAccessoryList = (accessories, productID) => {
  if (!productID) {
    return [];
  }
  return accessories?.filter((item) => item?.parentProductId === productID);
};

export const getSecondarySelectedAccessoriesPrice = (accessories, parentProductID) => {
  const accessoriesWRTParent = getSecondaryAccessoryList(accessories, parentProductID);
  let secondaryAccessoriesTotalOfferPrice = 0;
  let secondaryAccessoriesTotalListPrice = 0;
  if (accessoriesWRTParent.length > 0 && accessoriesWRTParent[0]?.skuInfo?.offerPrice) {
    secondaryAccessoriesTotalOfferPrice = accessoriesWRTParent[0]?.skuInfo?.offerPrice;
    secondaryAccessoriesTotalListPrice = accessoriesWRTParent[0]?.skuInfo?.listPrice;
  }
  return {
    secondaryAccessoriesTotalOfferPrice,
    secondaryAccessoriesTotalListPrice,
  };
};

export const getPriceWhenAccessoriesPresent = (
  prices,
  accessories,
  priceInfoAccessoryPresent,
  isAccessoryPresentMandatory,
  secondarySelectedAccessoryList = []
) => {
  let productPrices = {};
  if (isAccessoryPresentMandatory) {
    // If selectedAccessories primary or secondary in less than 1
    if (accessories?.length < 1) {
      const { minOfferPrice, maxOfferPrice, minListPrice, maxListPrice } =
        priceInfoAccessoryPresent || {};
      productPrices = {
        listPrice: prices.listPrice + minListPrice,
        offerPrice: prices.offerPrice + minOfferPrice,
        // checking if offerPrice and highOfferPrice are same then we don't need to show price range
        ...(prices.offerPrice + minOfferPrice !==
          getPriceFromPreviousPrice(prices.highOfferPrice, prices.offerPrice, maxOfferPrice) && {
          highOfferPrice: getPriceFromPreviousPrice(
            prices.highOfferPrice,
            prices.offerPrice,
            maxOfferPrice
          ),
        }),
        // checking if listPrice and highListPrice are same then we don't need to show price range
        ...(prices.listPrice + minListPrice !==
          getPriceFromPreviousPrice(prices.highListPrice, prices.listPrice, maxListPrice) && {
          highListPrice: getPriceFromPreviousPrice(
            prices.highListPrice,
            prices.listPrice,
            maxListPrice
          ),
        }),
        onlyListPrice: prices.onlyListPrice || prices.listPrice,
      };
    }
    // checking if accessory is selected then no need to show price range
    else {
      const { skuInfo } = accessories && accessories.length && accessories[0];
      const { productId } = skuInfo;
      const { listPrice: listPriceSelectedAccessory, offerPrice: offerPriceSelectedAccessory } =
        skuInfo;
      let secondaryAccessoryListPrice = 0;
      let secondaryAccessoryOfferPrice = 0;
      if (secondarySelectedAccessoryList?.length > 0) {
        const { secondaryAccessoriesTotalOfferPrice, secondaryAccessoriesTotalListPrice } =
          getSecondarySelectedAccessoriesPrice(secondarySelectedAccessoryList, productId);
        secondaryAccessoryListPrice = secondaryAccessoriesTotalListPrice;
        secondaryAccessoryOfferPrice = secondaryAccessoriesTotalOfferPrice;
      }
      productPrices = {
        listPrice: prices.listPrice + listPriceSelectedAccessory + secondaryAccessoryListPrice,
        offerPrice: prices.offerPrice + offerPriceSelectedAccessory + secondaryAccessoryOfferPrice,
      };
    }
    return productPrices;
  }
  return prices;
};

export const calculateFinalPrice = (
  offerPrice,
  discount,
  TCPPromotionPrice = '',
  storeSpecialPriceRange,
  defaultStoreId = ''
) => {
  const { minStorePrice, maxStorePrice, storeId } = storeSpecialPriceRange || {};
  const tcpPromoDiscount = TCPPromotionPrice
    ? ((offerPrice - TCPPromotionPrice) / offerPrice) * 100
    : 0;

  // Helper function to calculate price after discount
  const applyDiscount = (price, off) => ((Number(price) * (100 - off)) / 100).toFixed(2);

  const discountWrapper = (minSP, maxSP, disc = 0) => {
    return minSP === maxSP
      ? applyDiscount(minSP, disc)
      : `${applyDiscount(minSP, disc)} - ${applyDiscount(maxSP, disc)}`;
  };

  if (TCPPromotionPrice) {
    if (minStorePrice && maxStorePrice && defaultStoreId === storeId) {
      return discountWrapper(minStorePrice, maxStorePrice, tcpPromoDiscount);
    }
    return applyDiscount(TCPPromotionPrice, discount || 0);
  }

  if (minStorePrice && maxStorePrice && defaultStoreId === storeId) {
    return discountWrapper(minStorePrice, maxStorePrice);
  }

  return applyDiscount(offerPrice, discount || 0);
};

export const getIsAccessoryPresentMandatory = (
  minOfferPrice,
  maxOfferPrice,
  minListPrice,
  maxListPrice
) => {
  if (
    minOfferPrice !== null &&
    maxOfferPrice !== null &&
    minListPrice !== null &&
    maxListPrice !== null
  ) {
    return true;
  }
  return false;
};

export const getBrandName = (primaryBrandName, alternateBrandName) => {
  return primaryBrandName || alternateBrandName || '';
};

export const getCurrentProductBadges = ({ backAB, allProductId, colorDisplayId }) => {
  if (backAB || !allProductId || !colorDisplayId) {
    return '';
  }
  const productBadges = allProductId?.find(
    (product) => product.productid === colorDisplayId
  )?.product_badge;
  return productBadges || '';
};
export const getParsedProductBadges = (productBadges, mobileApp = false) => {
  if (!productBadges) {
    return null;
  }
  try {
    const parsedProductBadges = mobileApp
      ? JSON.parse(JSON.stringify(productBadges))
      : JSON.parse(productBadges);
    const filteredBadges =
      parsedProductBadges && parsedProductBadges.filter((badge) => badge.flag !== false);
    return filteredBadges && filteredBadges.slice(0, MAX_NO_OF_ESG_BADGES_TO_BE_DISPLAYED);
  } catch (e) {
    return null;
  }
};
export const getIsESGBadgeAnalyticsString = ({ backAB, allProductId, colorDisplayId }) => {
  const productBadges = getCurrentProductBadges({
    backAB,
    allProductId,
    colorDisplayId,
  });
  const parsedProductBadges = productBadges && getParsedProductBadges(productBadges);
  return parsedProductBadges && parsedProductBadges.length > 0 ? ESG_BADGE_ANALYTICS_STRING : '';
};

export const trimLongString = (parsedLongDescription, maxLimit = 184) => {
  if (parsedLongDescription?.length > maxLimit) {
    return parsedLongDescription.substring(0, maxLimit);
  }
  return parsedLongDescription;
};

const goToOutFit = (data) => {
  setTimeout(() => {
    const { bundle: { id = '', items = [] } = {} } = data;
    const ids = items.map((item) => item.remote_id);
    const idGroup = ids?.join('-');
    const outfitUrl = `/outfit?outfitId=${id}&vendorColorProductIdsList=${idGroup}&viaModule=Stylistics_Recommendation&viaPage=pdp`;
    const outfitPath = `/outfit/${id}/${idGroup}`;
    routerPush(outfitUrl, outfitPath);
  }, 0);
};

const goToPDP = (data) => {
  const { item: { affiliate_link: link = '' } = {} } = data;
  const [, pdpURL] = link.split('/p/');
  routerPush(`/p?pid=${pdpURL}`, `/p/${pdpURL}`, { shallow: true });
};

const bgScrollHandling = () => {
  setTimeout(() => {
    const { isDesktop } = getViewportInfo();
    const desktopCloseButton = document.querySelector('.stylitics-desktop-modal-close');
    const mobileCloseButton = document.querySelector('.stylitics-close');
    const closeButton = isDesktop ? desktopCloseButton : mobileCloseButton;

    const desktopPopupModal = document.querySelector('.stylitics-desktop-modal-scrim');
    const mobilePopupModal = document.querySelector('.stylitics-modal');
    const popupModal = isDesktop ? desktopPopupModal : mobilePopupModal;

    if (closeButton) {
      document.body.style.overflow = 'hidden';
      closeButton.addEventListener('click', () => {
        document.body.style.overflow = 'visible';
      });
      popupModal.addEventListener('click', () => {
        document.body.style.overflow = 'visible';
      });
    }
  }, 500);
};

const appendOverlay = (element) => {
  const badge = document.createElement('p');
  badge.textContent = 'Shop This Look >';
  element.querySelector('.stylitics-bundle-badge').appendChild(badge);
};

const showTitle = (bundles) => {
  const titleElement = document.getElementById('widget-title');
  if (bundles?.length) {
    titleElement.style.display = 'block';
  } else {
    titleElement.style.display = 'none';
  }
};

const setupWidgetHandlers = (widgetInstance, outfitV3WidgetVDABTestEnabled) => {
  const { isDesktop } = getViewportInfo() || {};

  if (!outfitV3WidgetVDABTestEnabled) {
    if (isDesktop) {
      widgetInstance.on('mount', 'bundle', (data) => {
        const { element } = data;
        if (element) {
          appendOverlay(element);
        }
      });
    }
    widgetInstance.override('click', 'bundle', (data) => data && goToOutFit(data));
  } else {
    widgetInstance.on('click', 'bundle', () => bgScrollHandling());
    widgetInstance.override('click', 'item', (data) => data && goToPDP(data));
  }
};

export function loadOutfitV3(
  productId,
  outfitV3WidgetVDABTestEnabled,
  outfitV3WidgetMnMABTest,
  setStyliticsProductTabListData,
  setSelectedProductIdForOutfit
) {
  const key = getStyliticsUserName();
  const outfitV3Widget = document.getElementById('stylitics-widget-container');
  if (outfitV3Widget) {
    outfitV3Widget.innerHTML = '';
    const widgetInstance = new window.StyliticsClassicWidget(key, 'stylitics-widget-container', {
      api: {
        item_number: productId,
        min: 1,
        max: 10,
      },
      text: {
        // Override the default text "View details".
        viewDetailsCTA: outfitV3WidgetVDABTestEnabled ? 'View Details' : ' ',
      },
      display: {
        disableMnM: outfitV3WidgetMnMABTest,
        clickableCarouselGutterWidth: 30,
      },
    });

    setTimeout(() => {
      window.StyliticsWidget = widgetInstance;
    }, 500);
    const setData = (bundles) => {
      setStyliticsProductTabListData({
        [productId]: bundles,
        errors: { [productId]: null },
        completed: { [productId]: false },
      });
      setSelectedProductIdForOutfit(productId);
    };

    widgetInstance.on('mount', 'bundles', (data) => {
      const { bundles } = data;
      if (bundles) {
        setData(bundles);
        showTitle(bundles);
      }
      document.body.style.overflow = 'visible';
    });

    setupWidgetHandlers(widgetInstance, outfitV3WidgetVDABTestEnabled);

    widgetInstance.start();
  }
}
